import type { MenuProps } from 'antd/es/menu';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import getItem from '../../lib/util/getMenuItem';
import { ReactComponent as ClientIcon } from '../../assets/icons/client.svg';
import { ReactComponent as DashboardIcon } from '../../assets/icons/dashboard.svg';
import { ReactComponent as SettingIcon } from '../../assets/icons/setting.svg';
import { ReactComponent as TestIcon } from '../../assets/icons/test.svg';
import { ReactComponent as SelctedClient } from '../../assets/icons/selectedClient.svg';
import { ReactComponent as SelectedDashboard } from '../../assets/icons/selectedDashboard.svg';
import { ReactComponent as SelectedSetting } from '../../assets/icons/selectedSetting.svg';
import { ReactComponent as SelectedTest } from '../../assets/icons/selectedTest.svg';
import { useTheme } from 'styled-components';
import { camelize } from '../../lib/util/stringUtils';

const useSiderController = () => {
  const theme = useTheme();
  const rootSubmenuKeys = ['patient', 'test', 'setting'];
  const menuLabels = {
    dashboard: <Link to={'/clinic/dashboard'}>{theme.menus.dashboard}</Link>,
    patientList: (
      <Link to={'/clinic/patient/list'}>{theme.menus.patientList}</Link>
    ),
    patientAdd: (
      <Link to={'/clinic/patient/add'}>{theme.menus.addPatient}</Link>
    ),
    testInformation: (
      <Link to={'/clinic/test/information'}>{theme.menus.testInformation}</Link>
    ),
    testOrder: <Link to={'/clinic/test/order'}>{theme.menus.order}</Link>,
    testCheck: <Link to={'/clinic/test/check'}>{theme.menus.checkOrder}</Link>,
    settingClinic: (
      <Link to={'/clinic/setting/clinic'}>{theme.menus.clinicInformation}</Link>
    ),
    settingStaffList: (
      <Link to={'/clinic/setting/staff/list'}>{theme.menus.staffList}</Link>
    ),
    settingNoticeList: (
      <Link to={'/clinic/setting/notice/list'}>{theme.menus.notice}</Link>
    ),
  };

  const location = useLocation();
  const path = location.pathname.split('/');
  const key = camelize(path.slice(2).join(' '));
  const [selectedKeys, setSelectedKeys] = useState<string[]>([key]);
  const [openKeys, setOpenKeys] = useState([path[2]]);

  useEffect(() => {
    setSelectedKeys([key]);
    setOpenKeys([path[2]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const menuItems = [
    {
      label: menuLabels['dashboard'],
      key: 'dashboard',
      icon: path[2] === 'dashboard' ? <SelectedDashboard /> : <DashboardIcon />,
    },
    {
      label: theme.menus.patient,
      key: rootSubmenuKeys[0],
      icon: path[2] === 'patient' ? <SelctedClient /> : <ClientIcon />,
      children: [
        getItem(menuLabels['patientList'], 'patientList'),
        getItem(menuLabels['patientAdd'], 'patientAdd'),
      ],
    },
    {
      label: theme.menus.test,
      key: rootSubmenuKeys[1],
      icon: path[2] === 'test' ? <SelectedTest /> : <TestIcon />,
      children: [
        getItem(menuLabels['testInformation'], 'testInformation'),
        getItem(menuLabels['testOrder'], 'testOrder'),
        getItem(menuLabels['testCheck'], 'testCheck'),
      ],
    },
    {
      label: theme.menus.setting,
      key: rootSubmenuKeys[2],
      icon: path[2] === 'setting' ? <SelectedSetting /> : <SettingIcon />,
      children: [
        getItem(menuLabels['settingClinic'], 'settingClinic'),
        getItem(menuLabels['settingStaffList'], 'settingStaffList'),
        getItem(menuLabels['settingNoticeList'], 'settingNoticeList'),
      ],
    },
  ];

  const onOpenChange: MenuProps['onOpenChange'] = (keys) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
    if (rootSubmenuKeys.indexOf(latestOpenKey!) === -1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  const onSelect = ({ key }: { key: string }): void => {
    setSelectedKeys([key]);
    if (key === 'dashboard') {
      setOpenKeys([]);
    }
  };

  return {
    menuItems,
    onOpenChange,
    selectedKeys,
    openKeys,
    onSelect,
  };
};

export default useSiderController;
